/**
 * Project Detail View
 *
 * Main workspace showing:
 * - Project information
 * - Team members
 * - Contributions table
 */

import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Chip,
  Select,
  MenuItem,
  FormControl,
  CircularProgress,
  Button,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useProject } from '../hooks/useProjects';
import { useReferenceData } from '../hooks/useReferenceData';
import { ProjectStatus, TeamMember } from '../types';
import { LoadingSpinner } from '../../../components/loading/LoadingStates';
import { ErrorDisplay } from '../../../components/errors/ErrorDisplay';
import UserBrowser, { User } from '../../../components/shared/UserBrowser';
import projectService from '../services/projectService';

const ProjectDetail: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();

  const {
    project: hookProject,
    etag,
    loading,
    error,
    loadProject
  } = useProject(projectId || null);
  const { referenceData } = useReferenceData();

  // Local project state that we can update immediately
  const [localProject, setLocalProject] = useState(hookProject);

  // Track editing state for team member fields
  const [editValues, setEditValues] = useState<Record<string, { role: string; contribution: string }>>({});

  // Track pending team members being added (optimistic UI)
  const [pendingMembers, setPendingMembers] = useState<TeamMember[]>([]);

  // Key to force UserBrowser reset
  const [userBrowserKey, setUserBrowserKey] = useState(0);

  // Sync local project with hook project
  React.useEffect(() => {
    if (hookProject) {
      setLocalProject(hookProject);
    }
  }, [hookProject]);

  // Use local project as the source of truth
  const project = localProject;

  // Track editing state for project fields
  const [projectEditValues, setProjectEditValues] = useState({
    projectName: '',
    client: '',
    product: '',
    therapeuticArea: '',
    indication: '',
    activity: '',
    subActivity: '',
    projectStart: '',
    projectEnd: '',
    description: '',
  });

  // Track dirty state and validation errors
  const [dirtyFields, setDirtyFields] = useState<Set<string>>(new Set());
  const [saveInProgress, setSaveInProgress] = useState(false);
  const [dateValidationError, setDateValidationError] = useState<string>('');

  // Calculate project status based on dates
  const getProjectStatus = (): ProjectStatus => {
    if (!project) return ProjectStatus.ACTIVE;

    if (project.projectEnd) {
      const endDate = new Date(project.projectEnd);
      const now = new Date();
      if (endDate <= now) {
        return ProjectStatus.COMPLETED;
      }
    }

    return ProjectStatus.ACTIVE;
  };

  // Get status chip color
  const getStatusColor = (status: ProjectStatus) => {
    switch (status) {
      case ProjectStatus.ACTIVE:
        return 'success';
      case ProjectStatus.COMPLETED:
        return 'default';
      default:
        return 'default';
    }
  };

  // Handle user selection from browser
  const handleUserSelection = async (users: User[]) => {
    if (users.length === 0 || !project || !projectId) return;

    const user = users[0];
    // Check if user already in team or pending
    if (project.team.some(m => m.userId === user.id) || pendingMembers.some(m => m.userId === user.id)) {
      return;
    }

    // Create new team member object
    const newMember: TeamMember = {
      userId: user.id,
      userName: user.displayName,
      role: '',
      contribution: '',
      assignedAt: new Date().toISOString(),
    };

    // Optimistically add to pending members
    setPendingMembers(prev => [...prev, newMember]);

    // Clear user browser immediately
    setUserBrowserKey(prev => prev + 1);

    // Add to backend - call service directly to avoid full page reload
    try {
      await projectService.addTeamMember(projectId, newMember);

      // Success - update local project state directly
      setLocalProject(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          team: [...prev.team, newMember]
        };
      });

      // Remove from pending
      setPendingMembers(prev => prev.filter(m => m.userId !== user.id));
    } catch (err) {
      console.error('Failed to add team member:', err);
      // Failed - remove from pending
      setPendingMembers(prev => prev.filter(m => m.userId !== user.id));
    }
  };

  // Initialize edit values when project loads
  React.useEffect(() => {
    if (project) {
      const values: Record<string, { role: string; contribution: string }> = {};
      project.team.forEach(member => {
        values[member.userId] = {
          role: member.role || '',
          contribution: member.contribution || ''
        };
      });
      setEditValues(values);

      // Initialize project field values
      setProjectEditValues({
        projectName: project.projectName,
        client: project.client,
        product: project.product || '',
        therapeuticArea: project.therapeuticArea,
        indication: project.indication || '',
        activity: project.activity,
        subActivity: project.subActivity || '',
        projectStart: project.projectStart.split('T')[0],
        projectEnd: project.projectEnd ? project.projectEnd.split('T')[0] : '',
        description: project.description,
      });
    }
  }, [project]);

  // Handle field change
  const handleFieldChange = (userId: string, field: 'role' | 'contribution', value: string) => {
    setEditValues(prev => ({
      ...prev,
      [userId]: {
        ...prev[userId],
        [field]: value
      }
    }));
  };

  // Handle field update on blur
  const handleFieldUpdate = async (userId: string, field: 'role' | 'contribution', value: string, originalValue: string) => {
    if (value === originalValue || !projectId) return;

    try {
      // Call service directly to avoid full reload
      await projectService.updateTeamMember(projectId, userId, { [field]: value });

      // Update local state
      setLocalProject(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          team: prev.team.map(m =>
            m.userId === userId ? { ...m, [field]: value } : m
          )
        };
      });
    } catch (err) {
      console.error(`Failed to update ${field}:`, err);
      // Revert on error
      setEditValues(prev => ({
        ...prev,
        [userId]: {
          ...prev[userId],
          [field]: originalValue
        }
      }));
    }
  };

  // Handle project field change (mark as dirty, don't auto-save)
  const handleProjectFieldChange = (field: keyof typeof projectEditValues, value: string) => {
    setProjectEditValues(prev => {
      const updates: any = { [field]: value };

      // If therapeutic area changes, check if current indication is still valid
      if (field === 'therapeuticArea' && referenceData) {
        const validIndications = referenceData.indications.filter(
          ind => !ind.therapeuticArea || ind.therapeuticArea === value
        );
        // If current indication is not valid for new TA, clear it
        if (prev.indication && !validIndications.some(ind => ind.code === prev.indication)) {
          updates.indication = '';
          setDirtyFields(prev => new Set(prev).add('indication'));
        }
      }

      return { ...prev, ...updates };
    });

    // Mark field as dirty
    setDirtyFields(prev => new Set(prev).add(field));

    // Validate dates (compare as strings in YYYY-MM-DD format)
    if (field === 'projectEnd' && value) {
      const startDate = projectEditValues.projectStart;
      if (startDate && value < startDate) {
        setDateValidationError('Completion date must be after start date');
      } else {
        setDateValidationError('');
      }
    } else if (field === 'projectStart') {
      const endDate = projectEditValues.projectEnd;
      if (endDate && endDate < value) {
        setDateValidationError('Completion date must be after start date');
      } else {
        setDateValidationError('');
      }
    }
  };

  // Save all dirty project fields at once
  const handleSaveChanges = async () => {
    if (dirtyFields.size === 0 || !projectId || !project || dateValidationError) return;

    setSaveInProgress(true);
    try {
      // Build update request with only dirty fields
      const updates: any = {};

      dirtyFields.forEach(field => {
        const value = projectEditValues[field as keyof typeof projectEditValues];

        // Convert date fields to ISO format
        if (field === 'projectStart' || field === 'projectEnd') {
          if (value) {
            updates[field] = new Date(value).toISOString();
          } else if (field === 'projectEnd') {
            updates[field] = null; // Explicitly clear optional end date
          }
        } else if (field === 'subActivity' && !value) {
          updates[field] = null; // Explicitly clear optional subActivity
        } else if (field === 'indication' && !value) {
          updates[field] = null; // Explicitly clear optional indication
        } else if (field === 'product' && !value) {
          updates[field] = null; // Explicitly clear optional product
        } else {
          updates[field] = value || undefined;
        }
      });

      // Call service to save all updates
      await projectService.updateProject(projectId, updates, etag);

      // Reload project to get fresh ETag and data
      await loadProject();

      // Clear dirty fields
      setDirtyFields(new Set());
    } catch (err) {
      console.error('Failed to save project changes:', err);
    } finally {
      setSaveInProgress(false);
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading project..." />;
  }

  if (error || !project) {
    return (
      <Box sx={{ p: 3 }}>
        <ErrorDisplay
          title="Failed to load project"
          error={error || 'Project not found'}
          onRetry={loadProject}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/projects')} sx={{ mr: 2, color: 'white' }}>
          <BackIcon />
        </IconButton>
        <TextField
          fullWidth
          value={projectEditValues.projectName}
          onChange={(e) => handleProjectFieldChange('projectName', e.target.value)}
          variant="standard"
          sx={{
            flexGrow: 1,
            '& .MuiInput-input': {
              color: 'white',
              fontSize: '2.125rem',
              fontWeight: 400,
              lineHeight: 1.235,
            },
            '& .MuiInput-underline:before': {
              borderBottomColor: 'rgba(255, 255, 255, 0.3)',
            },
            '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
              borderBottomColor: 'rgba(255, 255, 255, 0.5)',
            },
            '& .MuiInput-underline:after': {
              borderBottomColor: 'white',
            },
          }}
        />
        <Tooltip title="Refresh">
          <IconButton onClick={loadProject} disabled={loading} sx={{ color: 'white', ml: 2 }}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Project Info */}
      <Paper sx={{ p: 2.5, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6">Project Information</Typography>
            <Chip
              label={getProjectStatus()}
              color={getStatusColor(getProjectStatus())}
              size="small"
            />
          </Box>
          <Button
            variant="contained"
            size="small"
            onClick={handleSaveChanges}
            disabled={dirtyFields.size === 0 || saveInProgress || !!dateValidationError}
          >
            {saveInProgress ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>

        <Grid container spacing={2}>
          {/* First Row - Core Info */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography variant="caption" color="text.secondary" display="block">Client</Typography>
            <TextField
              size="small"
              fullWidth
              value={projectEditValues.client}
              onChange={(e) => handleProjectFieldChange('client', e.target.value)}
              variant="standard"
              InputProps={{ style: { fontSize: '0.875rem', fontWeight: 500 } }}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography variant="caption" color="text.secondary" display="block">Product</Typography>
            <TextField
              size="small"
              fullWidth
              value={projectEditValues.product}
              onChange={(e) => handleProjectFieldChange('product', e.target.value)}
              placeholder="Optional"
              variant="standard"
              InputProps={{ style: { fontSize: '0.875rem', fontWeight: 500 } }}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography variant="caption" color="text.secondary" display="block">Therapeutic Area</Typography>
            <FormControl fullWidth variant="standard" size="small">
              <Select
                value={projectEditValues.therapeuticArea}
                onChange={(e) => handleProjectFieldChange('therapeuticArea', e.target.value)}
                sx={{ fontSize: '0.875rem', fontWeight: 500 }}
              >
                {referenceData?.therapeuticAreas.map((ta) => (
                  <MenuItem key={ta.code} value={ta.code}>
                    {ta.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography variant="caption" color="text.secondary" display="block">Indication</Typography>
            <FormControl fullWidth variant="standard" size="small">
              <Select
                value={projectEditValues.indication}
                onChange={(e) => handleProjectFieldChange('indication', e.target.value)}
                sx={{ fontSize: '0.875rem', fontWeight: 500 }}
              >
                {referenceData?.indications
                  .filter(ind => !ind.therapeuticArea || ind.therapeuticArea === projectEditValues.therapeuticArea)
                  .map((ind) => (
                    <MenuItem key={ind.code} value={ind.code}>
                      {ind.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Second Row - Dates & Activity */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography variant="caption" color="text.secondary" display="block">Start Date</Typography>
            <TextField
              size="small"
              fullWidth
              type="date"
              value={projectEditValues.projectStart}
              onChange={(e) => handleProjectFieldChange('projectStart', e.target.value)}
              variant="standard"
              InputProps={{ style: { fontSize: '0.875rem', fontWeight: 500 } }}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography variant="caption" color="text.secondary" display="block">Completion Date</Typography>
            <TextField
              size="small"
              fullWidth
              type="date"
              value={projectEditValues.projectEnd}
              onChange={(e) => handleProjectFieldChange('projectEnd', e.target.value)}
              error={!!dateValidationError}
              helperText={dateValidationError}
              placeholder="Optional"
              variant="standard"
              InputProps={{ style: { fontSize: '0.875rem', fontWeight: 500 } }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography variant="caption" color="text.secondary" display="block">Activity</Typography>
            <TextField
              size="small"
              fullWidth
              value={projectEditValues.activity}
              onChange={(e) => handleProjectFieldChange('activity', e.target.value)}
              placeholder="e.g., Medical Writing"
              variant="standard"
              InputProps={{ style: { fontSize: '0.875rem', fontWeight: 500 } }}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography variant="caption" color="text.secondary" display="block">Sub-Activity</Typography>
            <TextField
              size="small"
              fullWidth
              value={projectEditValues.subActivity}
              onChange={(e) => handleProjectFieldChange('subActivity', e.target.value)}
              placeholder="Optional"
              variant="standard"
              InputProps={{ style: { fontSize: '0.875rem', fontWeight: 500 } }}
            />
          </Grid>

          {/* Description - Full Width */}
          <Grid size={{ xs: 12 }}>
            <Typography variant="caption" color="text.secondary" display="block">Description</Typography>
            <TextField
              size="small"
              fullWidth
              multiline
              rows={2}
              value={projectEditValues.description}
              onChange={(e) => handleProjectFieldChange('description', e.target.value)}
              placeholder="Project description..."
              variant="standard"
              InputProps={{ style: { fontSize: '0.875rem', fontWeight: 500 } }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Team Members */}
      <Grid container spacing={3}>
        {/* User Browser */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Add Team Members
            </Typography>
            <UserBrowser
              key={userBrowserKey}
              multiSelect={false}
              onSelectionChange={handleUserSelection}
              itemsPerPage={5}
            />
          </Paper>
        </Grid>

        {/* Team Table */}
        <Grid size={{ xs: 12, md: 7 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Team Members ({project.team.length + pendingMembers.length})
            </Typography>

            {project.team.length === 0 && pendingMembers.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 8,
                  color: 'text.secondary',
                }}
              >
                <Typography variant="body1">
                  No team members yet. Select users from the browser to add them.
                </Typography>
              </Box>
            ) : (
              <TableContainer key={project.team.length}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Contribution</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Existing team members */}
                    {project.team.map((member) => {
                      const editValue = editValues[member.userId] || { role: member.role || '', contribution: member.contribution || '' };

                      return (
                        <TableRow key={member.userId} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {member.userName}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              fullWidth
                              value={editValue.role}
                              onChange={(e) => handleFieldChange(member.userId, 'role', e.target.value)}
                              onBlur={(e) => handleFieldUpdate(member.userId, 'role', e.target.value, member.role || '')}
                              placeholder="e.g., Medical Writer"
                              variant="standard"
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              fullWidth
                              multiline
                              maxRows={3}
                              value={editValue.contribution}
                              onChange={(e) => handleFieldChange(member.userId, 'contribution', e.target.value)}
                              onBlur={(e) => handleFieldUpdate(member.userId, 'contribution', e.target.value, member.contribution || '')}
                              placeholder="Describe contribution..."
                              variant="standard"
                            />
                          </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Remove">
                            <IconButton
                              size="small"
                              onClick={async () => {
                                if (window.confirm(`Remove ${member.userName} from team?`)) {
                                  try {
                                    if (!projectId) return;
                                    // Call service directly to avoid full reload
                                    await projectService.removeTeamMember(projectId, member.userId);

                                    // Update local state
                                    setLocalProject(prev => {
                                      if (!prev) return prev;
                                      return {
                                        ...prev,
                                        team: prev.team.filter(m => m.userId !== member.userId)
                                      };
                                    });
                                  } catch (err) {
                                    console.error('Failed to remove team member:', err);
                                  }
                                }
                              }}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                    })}

                    {/* Pending team members */}
                    {pendingMembers.map((member) => (
                      <TableRow key={`pending-${member.userId}`}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" fontWeight="medium" color="text.secondary">
                              {member.userName}
                            </Typography>
                            <CircularProgress size={16} />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.disabled">—</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.disabled">—</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color="text.disabled">—</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProjectDetail;
